import type {
    StripePaymentInfo,
    StripeProductsMode,
    StripeProductsResponse,
    StripeSubscriptionResponse,
} from '~/@types/backend-types'

import {
    getRequestStripeCustomerExists,
    getRequestStripePaymentMethod,
    getRequestStripeProducts,
    getRequestStripeSubscriptions,
    postRequestStripeCreateCustomer,
    postRequestStripeCreateSetupIntent,
    postRequestStripeFinishSetupIntent,
    postRequestStripeV1Purchase,
    putRequestStripeV1Purchase,
} from '@capture/client-api/src/orval'

import type { ServiceDict } from '../externals'

import { APIService } from './APIService'

export class StripeService extends APIService {
    constructor(services: ServiceDict, authToken: string) {
        super('appHost', services, authToken)
    }

    public async checkCustomerExists() {
        const { commonParams, requestOptions } = this.getRequestContext()
        return getRequestStripeCustomerExists(commonParams, requestOptions)
    }

    public async createCustomer({ name, email }: { name: string; email: string }) {
        const { commonParams, requestOptions } = this.getRequestContext()
        return postRequestStripeCreateCustomer(
            {
                ...commonParams,
                name,
                email,
            },
            requestOptions,
        )
    }

    public async createSetupIntent() {
        const { commonParams, requestOptions } = this.getRequestContext()
        return postRequestStripeCreateSetupIntent(commonParams, requestOptions)
    }

    public async finishSetupIntent(setupIntentId: string) {
        const { commonParams, requestOptions } = this.getRequestContext()
        return postRequestStripeFinishSetupIntent(
            {
                ...commonParams,
                setup_intent_id: setupIntentId,
            },
            requestOptions,
        )
    }

    public async getAllSubscriptions() {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestStripeSubscriptions(commonParams, requestOptions)

        return response.data as StripeSubscriptionResponse
    }

    public async getStripeProducts(
        mode: StripeProductsMode /* 'test'|'production'*/,
    ): Promise<StripeProductsResponse> {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestStripeProducts(
            {
                ...commonParams,
                [mode]: true,
            },
            requestOptions,
        )

        return response.data as StripeProductsResponse
    }

    public async getStripePaymentMethodInfo() {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await getRequestStripePaymentMethod(commonParams, requestOptions)

        return response.data as StripePaymentInfo
    }

    public async purchaseStripePlan(plan: string) {
        const { commonParams, requestOptions } = this.getRequestContext()
        return postRequestStripeV1Purchase(
            {
                ...commonParams,
                plan,
            },
            requestOptions,
        )
    }

    public async updateStripePlan(plan: string) {
        const { commonParams, requestOptions } = this.getRequestContext()
        const response = await putRequestStripeV1Purchase(
            {
                ...commonParams,
                plan,
            },
            requestOptions,
        )

        return response.data
    }
}
